import moment from "moment-timezone";
import {
  Button,
  Label,
  LabelColor,
  Modal,
  Spinner,
  Table,
  TableColumnProps,
  TableRow,
} from "north.js";
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import { errorToast, successToast } from "src/shared/components/Toast";
import { mediaQueries } from "src/theme/deviceBreakpoints";

import { withBuffer } from "../../shared/utils/buffer";
import { useDeleteShiftSignupMutation } from "../graphql/deleteShiftSignup.generated";
import {
  GetVolunteerShiftsQuery,
  useGetVolunteerShiftsLazyQuery,
} from "../graphql/getVolunteerShifts.generated";
import { Volunteer } from "../index";

import RoleLabels from "./RoleLabels";

interface VolunteerRole {
  isPrimary: boolean;
  name: string;
}

const PRIMARY_ROLES = ["Floor Patrol", "Security", "Supplementary"];

enum Status {
  REGISTERED = "Registered",
  CHECKED_IN = "Checked In",
  COMPLETED = "Completed",
  INCOMPLETE = "Incomplete",
}

const statusColor: Record<Status, LabelColor> = {
  [Status.REGISTERED]: "primary-light",
  [Status.CHECKED_IN]: "secondary-light",
  [Status.COMPLETED]: "success-light",
  [Status.INCOMPLETE]: "danger-light",
};

const UPCOMING_TABLE_COLUMNS: TableColumnProps[] = [
  { id: "activity", name: "ACTIVITY" },
  { id: "date", name: "DATE" },
  { id: "time", name: "TIME" },
  { id: "status", name: "STATUS" },
  { id: "unregister", name: "UNREGISTER", hideLabel: true },
];

const COMPLETED_TABLE_COLUMNS: TableColumnProps[] = [
  { id: "activity", name: "ACTIVITY" },
  { id: "date", name: "DATE" },
  { id: "time", name: "TIME" },
  { id: "roles", name: "ROLES" },
  { id: "status", name: "STATUS" },
];

const parseVolunteerShiftRoles = (data: GetVolunteerShiftsQuery) =>
  new Map<number, VolunteerRole>(
    data.volunteerShifts.map(({ shift_id, category }) => [
      shift_id,
      { name: category.name, isPrimary: category.primary_only },
    ])
  );

const getDate = (date: Date) => {
  return moment(date).tz("America/Toronto").format("ddd, MMM D");
};

const getHour = (date: Date) => {
  return moment(date).tz("America/Toronto").format("hA");
};

const getHourRange = (startTime: Date, endTime: Date) => {
  return `${getHour(startTime)} - ${getHour(endTime)}`;
};

const statusLabel = (checkedIn: boolean, endTime: Date) => {
  const status =
    endTime < new Date()
      ? checkedIn
        ? Status.COMPLETED
        : Status.INCOMPLETE
      : checkedIn
      ? Status.CHECKED_IN
      : Status.REGISTERED;
  return <Label color={statusColor[status]}>{status}</Label>;
};

interface VolunteerModalProps {
  volunteer: Volunteer;
  refetch: () => void;
  open: boolean;
  onClose: () => void;
  onEdit: () => void;
}

const VolunteerModal = ({
  volunteer,
  refetch,
  open,
  onClose,
  onEdit,
}: VolunteerModalProps) => {
  const [volunteerShiftRoles, setVolunteerShiftRoles] = useState<
    Map<number, VolunteerRole>
  >(new Map());

  const [deleteShiftSignupMutation] = useDeleteShiftSignupMutation();

  const deleteShiftSignup = useCallback(
    async (shiftId: number) => {
      try {
        await deleteShiftSignupMutation({
          variables: {
            event_attendee_id: volunteer.eventAttendeeId,
            shift_id: shiftId,
          },
        });
        successToast("Unregistered!");
        refetch();
      } catch (e) {
        if (e instanceof Error) {
          errorToast(e.message);
        } else {
          errorToast("An unknown error occurred");
        }
      }
    },
    [volunteer, deleteShiftSignupMutation, refetch]
  );

  const [getVolunteerShifts, { data, loading }] =
    useGetVolunteerShiftsLazyQuery();

  useEffect(() => {
    const shiftIds = volunteer.shifts.map(({ id }) => id);
    getVolunteerShifts({ variables: { shift_ids: shiftIds } });
  }, [volunteer.shifts, getVolunteerShifts]);

  useEffect(() => {
    if (data && !loading) {
      setVolunteerShiftRoles(parseVolunteerShiftRoles(data));
    }
  }, [data, loading]);

  const bufferedShifts = withBuffer(
    volunteer.shifts.map(({ id, startTime, endTime, checkedIn }) => ({
      shiftId: id,
      start: startTime,
      end: endTime,
      checkedIn,
    }))
  );
  const bufferedById = new Map(
    bufferedShifts.map((shift) => [shift.shiftId, shift])
  );

  const upcomingTableData: TableRow[] = volunteer.shifts
    .filter(({ endTime }) => endTime >= new Date())
    .map(({ id, name, checkedIn, startTime, endTime }) => {
      const buffered = bufferedById.get(id);
      const displayEnd = buffered?.effectiveEnd ?? endTime;
      return {
        activity: name,
        date: getDate(startTime),
        time: getHourRange(startTime, displayEnd),
        status: statusLabel(checkedIn, endTime),
        unregister: (
          <Button
            key={`unregister-${id}`}
            color="secondary"
            onClick={() => deleteShiftSignup(id)}
          >
            Unregister
          </Button>
        ),
      };
    });

  const completedTableData: TableRow[] = volunteer.shifts
    .filter(({ endTime }) => endTime < new Date())
    .map(({ id, name, checkedIn, startTime, endTime }) => {
      let roles: string[] = [];
      const role = volunteerShiftRoles.get(id);
      const buffered = bufferedById.get(id);
      const displayEnd = buffered?.effectiveEnd ?? endTime;

      if (role) {
        const { isPrimary, name } = role;

        roles =
          isPrimary || PRIMARY_ROLES.includes(name)
            ? [name]
            : ["Supplementary", name];
      }

      return {
        activity: name,
        date: getDate(startTime),
        time: getHourRange(startTime, displayEnd),
        roles: <RoleLabels key={`shift-roles-${id}`} roles={roles} />,
        status: statusLabel(checkedIn, endTime),
      };
    });

  return (
    <StyledModal open={open} onClose={onClose}>
      <Modal.Header actions={<Modal.CloseButton onClick={onClose} />}>
        Volunteer Profile
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <Spinner />
        ) : (
          <InfoContainer>
            <Info>
              <LargeBodyBold>Full Name</LargeBodyBold>
              <Body>{volunteer.name}</Body>
            </Info>
            <Info>
              <LargeBodyBold>Email</LargeBodyBold>
              <Body>{volunteer.email}</Body>
            </Info>
            <Info>
              <LargeBodyBold>Roles</LargeBodyBold>
              <Body>
                <RoleLabels
                  roles={[volunteer.primaryRole, ...volunteer.additionalRoles]}
                  direction="column"
                />
              </Body>
            </Info>
            <Info>
              <LargeBodyBold>Upcoming Shifts</LargeBodyBold>
              <Table
                columns={UPCOMING_TABLE_COLUMNS}
                data={upcomingTableData}
                showFooter
                horizontalPadding={16}
                verticalPadding={6}
              />
            </Info>
            <Info>
              <LargeBodyBold>Past Shifts</LargeBodyBold>
              <Body>{`Total Hours Completed: ${volunteer.hoursCompleted}`}</Body>
              <Table
                columns={COMPLETED_TABLE_COLUMNS}
                data={completedTableData}
                showFooter
                horizontalPadding={16}
                verticalPadding={6}
              />
            </Info>
          </InfoContainer>
        )}
      </Modal.Body>
      <Modal.Footer justify="end">
        <Button color="secondary" onClick={onEdit}>
          Edit
        </Button>
      </Modal.Footer>
    </StyledModal>
  );
};

export default VolunteerModal;

const StyledModal = styled(Modal)`
  min-width: 766px;
  overflow: auto;
  max-height: 90vh;

  ${mediaQueries.tablet} {
    width: 100%;
  }
`;

const InfoContainer = styled.div`
  padding: 12px 0px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
`;

const Info = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const LargeBodyBold = styled.p`
  color: var(--Secondary-Dark-Blue, #0e3460);
  /* Desktop/Large Body Bold */
  font-size: 20px;
  font-style: normal;
  font-weight: 700;
  line-height: 160%; /* 32px */
`;

const Body = styled.p`
  color: var(--Secondary-Dark-Blue, #0e3460);
  /* Desktop/body */
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 180%; /* 28.8px */
`;
